import React, { useState, useRef, useEffect } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import handlePng from '../Icons/assets/pngs/knob.png';

interface HandleProps {
  value: number;
  updateValue: (newValue: number) => void;
  min: number;
  max: number;
  trackRef: React.RefObject<HTMLDivElement>;
  integer?: boolean;
  decimal?: boolean;
}

const Handle: React.FC<HandleProps> = ({
  value,
  min,
  max,
  updateValue,
  trackRef,
  integer,
  decimal,
}) => {
  const calculateHandlePosition = (value: number) =>
    `calc(${((value - min) / (max - min)) * 100}% - 9px)`;

  const position = calculateHandlePosition(value);

  const handleMouseMove = (e: globalThis.MouseEvent) => {
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      let newValue = ((e.clientX - rect.left) / rect.width) * (max - min) + min;

      if (integer) {
        newValue = Math.round(newValue);
      } else if (decimal) {
        newValue = Math.round(newValue * 20) / 20; // Round to nearest 0.05 for decimal
      } else {
        newValue = Math.round(newValue * 4) / 4;
      }
      updateValue(newValue);
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = () => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      className="absolute left-0 top-0 z-[2] h-[18px] w-[18px] cursor-pointer bg-center bg-no-repeat outline-none"
      style={{ left: position, backgroundImage: `url(${handlePng})` }}
      onMouseDown={handleMouseDown}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          if (integer) {
            updateValue(value + 1);
          } else if (decimal) {
            updateValue(value + 0.05);
          } else {
            updateValue(value + 0.25);
          }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          if (integer) {
            updateValue(value - 1);
          } else if (decimal) {
            updateValue(value - 0.05);
          } else {
            updateValue(value - 0.25);
          }
        }
      }}
    />
  );
};

export interface RangeSliderV2Props {
  min: number;
  max: number;
  defaultValue: [number, number];
  onChange: (values: [number, number]) => void;
  displayFormat?: (value: number) => string;
  inputFormat?: (value: string) => number;
  displayPercentage?: boolean;
  integer?: boolean;
  decimal?: boolean;
}

const RangeSliderV2: React.FC<RangeSliderV2Props> = ({
  min,
  max,
  defaultValue,
  onChange,
  displayFormat,
  inputFormat,
  displayPercentage,
  integer,
  decimal,
}) => {
  const [value1, setValue1] = useState<number>(defaultValue[0]);
  const [value2, setValue2] = useState<number>(defaultValue[1]);
  const trackRef = useRef<HTMLDivElement>(null);
  const format = displayFormat || ((value: number) => value.toString());

  const debouncedOnChange = useDebouncedCallback(onChange, 100);

  const updateValue1 = (proposedValue: number) => {
    let newValue = Math.min(Math.max(proposedValue, min), value2);
    if (integer) {
      newValue = Math.round(newValue);
    } else if (decimal) {
      newValue = Math.round(newValue * 100) / 100;
    }

    setValue1(newValue);
    debouncedOnChange([newValue, value2]);
  };

  const updateValue2 = (proposedValue: number) => {
    let newValue = Math.max(Math.min(proposedValue, max), value1);
    if (decimal) {
      newValue = Math.round(newValue * 100) / 100; // Round to nearest 0.05 for decimal
    } else if (integer) {
      newValue = Math.round(newValue);
    }

    setValue2(newValue);
    debouncedOnChange([value1, newValue]);
  };

  const handleMinClick = () => {
    updateValue1(min);
  };

  const handleMaxClick = () => {
    updateValue2(max);
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      let clickPosition = ((e.clientX - rect.left) / rect.width) * (max - min) + min;

      if (decimal) {
        clickPosition = Math.round(clickPosition * 100) / 100; // Round to nearest 0.05 for decimal
      } else if (integer) {
        clickPosition = Math.round(clickPosition);
      }

      if (clickPosition < value1) {
        updateValue1(clickPosition);
      } else if (clickPosition > value2) {
        updateValue2(clickPosition);
      } else {
        const distanceToValue1 = Math.abs(value1 - clickPosition);
        const distanceToValue2 = Math.abs(value2 - clickPosition);

        if (distanceToValue1 < distanceToValue2) {
          updateValue1(clickPosition);
        } else {
          updateValue2(clickPosition);
        }
      }
    }
  };

  const rangeClassName = `left-[${((value1 - min) / (max - min)) * 100}%] width=[${((value2 - value1) / (max - min)) * 100}%]`;

  const left = ((value1 - min) / (max - min)) * 100;
  const width = ((value2 - value1) / (max - min)) * 100;
  const midpoint = left + width / 2;

  // const labelContainerClasses = `absolute flex justify-between text-brand-400 w-min min-w-[${widthPct}%] left-[${midpoint / 2}%]`;
  const labelContainerClasses = `absolute flex justify-between text-brand-400 w-min`;

  return (
    <div className="relative flex w-full select-none items-end justify-between">
      <div
        className="!cursor-pointer font-[Heebo,sans-serif] text-[10.5px] font-normal leading-[12.6px] text-[#d9dce4]"
        onClick={handleMinClick}
      >
        {displayPercentage ? format(min) + '%' : format(min)}
      </div>
      <div className="relative mx-[15px] flex h-[40px] flex-1 flex-col">
        <div
          className={labelContainerClasses}
          style={{
            left: `${midpoint}%`,
            transform: 'translateX(-50%)',
            minWidth: `${width}%`,
          }}
        >
          <div className="translate-x-[50%]">
            <EditableValueDisplay
              value={format(value1)}
              onChange={updateValue1}
              displayPercentage={displayPercentage}
              integer={integer}
              decimal={decimal}
              inputFormat={inputFormat}
            />
          </div>
          <div className="absolute left-1/2 top-[5px] translate-x-[-50%] font-[Heebo,sans-serif] text-[10.5px] font-normal leading-[12.6px] text-[#bbc5d7]">
            to
          </div>
          <div className="translate-x-[50%]">
            <EditableValueDisplay
              value={format(value2)}
              onChange={updateValue2}
              displayPercentage={displayPercentage}
              integer={integer}
              decimal={decimal}
              inputFormat={inputFormat}
            />
          </div>
        </div>

        <div className="relative mt-6 h-5" ref={trackRef} onClick={handleTrackClick}>
          <div className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-[#3b4351]" />
          <Handle
            value={value1}
            min={min}
            max={max}
            updateValue={updateValue1}
            trackRef={trackRef}
            integer={integer}
            decimal={decimal}
          />
          <Handle
            value={value2}
            min={min}
            max={max}
            updateValue={updateValue2}
            trackRef={trackRef}
            integer={integer}
            decimal={decimal}
          />
          <div
            className={`pointer-events-none absolute top-1/2 z-[1] h-0.5 -translate-y-1/2 bg-white ${rangeClassName}`}
          />
        </div>
      </div>
      <div
        className="cursor-pointer font-[Heebo,sans-serif] text-[10.5px] font-normal leading-[12.6px] text-[#d9dce4]"
        onClick={handleMaxClick}
      >
        {displayPercentage ? format(max) + '%' : format(max)}
      </div>
    </div>
  );
};

const EditableValueDisplay: React.FC<{
  value: string;
  onChange: (newValue: number) => void;
  inputFormat?: (value: string) => number;
  displayPercentage?: boolean;
  integer?: boolean;
  decimal?: boolean;
}> = ({ value, onChange, displayPercentage, integer, decimal, inputFormat }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const submitEdit = () => {
    let newValue = inputFormat ? inputFormat(inputValue) : parseFloat(inputValue);

    if (decimal) {
      newValue = Math.round(newValue * 100) / 100; // Round to nearest 0.01 for decimal
    } else if (integer) {
      newValue = Math.round(newValue);
    } else {
      newValue = Math.round(newValue * 4) / 4;
    }

    if (isNaN(newValue)) {
      cancelEdit();
    } else {
      onChange(newValue);
      setIsEditing(false);
    }
  };

  const handleValueBlur = () => {
    submitEdit();
  };

  const handleValueKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      submitEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const beginEdit = () => {
    setInputValue(value.toString());
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setInputValue(value.toString());
    setIsEditing(false);
  };

  return isEditing ? (
    <input
      className="absolute top-0 h-[20px] w-[50px] translate-x-[-50%] border border-[#333] bg-[#222] text-center font-[Heebo,sans-serif] text-[12px] font-normal leading-[20px] text-[#e85ee5] focus:outline focus:outline-1 focus:outline-[#333333]"
      type="text"
      ref={inputRef}
      value={inputValue}
      onChange={handleValueChange}
      onBlur={handleValueBlur}
      onKeyDown={handleValueKeyDown}
      onFocus={(e) => e.target.select()}
    />
  ) : (
    <span
      className="absolute top-[0] w-[50px] w-min border !border-none text-center font-heebo text-[12px] text-[12px] font-normal leading-[20px] text-brand-400 hover:cursor-pointer hover:border-[#333]"
      onClick={() => beginEdit()}
    >
      {value}
      {displayPercentage ? '%' : null}
    </span>
  );
};

export default RangeSliderV2;
